import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, fmtErr } from "@/lib/api";
import { Button, IconButton, TextField, Select, Panel, PageHead, EmptyState } from "@/sentinel";

const CADENCE = ["monthly", "one-time", "usage", "annual"];
const CADENCE_OPTS = CADENCE.map((c) => ({ value: c, label: c }));
// Costs keep cents (2dp), unlike other USD figures.
const usd2 = (v) => {
  const n = Number(v) || 0;
  return (n < 0 ? "−$" : "$") + Math.abs(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const RAMP = ["var(--viz-seq-5)", "var(--viz-seq-4)", "var(--viz-seq-3)", "var(--viz-seq-2)"];

function CostRow({ c, canEdit, refresh }) {
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ item: c.item, category: c.category, amount: c.amount, cadence: c.cadence, note: c.note });
  const save = async () => {
    try { await api.pmUpdateCost(c.id, { ...f, amount: Number(f.amount) }); setEditing(false); refresh(); toast.success("Cost updated."); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };
  const del = async () => { try { await api.pmDeleteCost(c.id); refresh(); } catch (e) { toast.error(e.message); } };

  if (editing) return (
    <tr data-testid={`pm-cost-${c.id}`}>
      <td><TextField aria-label="Item" value={f.item} onChange={(e) => setF({ ...f, item: e.target.value })} /></td>
      <td><TextField aria-label="Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} style={{ width: 112 }} /></td>
      <td className="r"><TextField aria-label="Amount" type="number" step="0.01" numeric value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} style={{ width: 96 }} /></td>
      <td><Select aria-label="Cadence" options={CADENCE_OPTS} value={f.cadence} onChange={(v) => setF({ ...f, cadence: v })} width={120} /></td>
      <td><TextField aria-label="Note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></td>
      <td>
        <div className="sn-row" style={{ gap: "var(--space-1)", flexWrap: "nowrap" }}>
          <IconButton icon="Check" label="Save cost" size="sm" onClick={save} />
          <IconButton icon="X" label="Cancel editing" size="sm" onClick={() => setEditing(false)} />
        </div>
      </td>
    </tr>
  );
  return (
    <tr data-testid={`pm-cost-${c.id}`}>
      <td style={{ color: "var(--text-primary)" }}>{c.item}</td>
      <td>{c.category}</td>
      <td className="num">{usd2(c.amount)}</td>
      <td>{c.cadence}</td>
      <td style={{ whiteSpace: "normal" }}>{c.note}</td>
      <td>
        {canEdit && (
          <div className="sn-row" style={{ gap: "var(--space-1)", flexWrap: "nowrap" }}>
            <IconButton icon="Pencil" label={`Edit ${c.item}`} size="sm" onClick={() => setEditing(true)} />
            <IconButton icon={Trash2} label={`Delete ${c.item}`} size="sm" onClick={del} />
          </div>
        )}
      </td>
    </tr>
  );
}

const EMPTY_FORM = { item: "", category: "Market data", amount: 0, cadence: "monthly", note: "" };

export default function Costs() {
  const { can } = useAuth();
  const canEdit = can("analyst");
  const qc = useQueryClient();
  const [form, setForm] = useState(EMPTY_FORM);
  const { data: rows = [], refetch } = useQuery({ queryKey: ["pm-costs"], queryFn: api.pmCosts });

  const monthlyRows = rows.filter((r) => r.cadence === "monthly");
  const monthly = monthlyRows.reduce((s, r) => s + (r.amount || 0), 0);
  const byCategory = Object.entries(
    monthlyRows.reduce((acc, r) => ({ ...acc, [r.category || "Other"]: (acc[r.category || "Other"] || 0) + (r.amount || 0) }), {}),
  ).sort((a, b) => b[1] - a[1]);
  const refresh = () => { refetch(); qc.invalidateQueries({ queryKey: ["pm-summary"] }); };

  const add = async () => {
    if (!form.item.trim()) { toast.error("Item required."); return; }
    try { await api.pmAddCost({ ...form, amount: Number(form.amount) }); setForm(EMPTY_FORM); refresh(); toast.success("Cost added."); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="sn-stack" style={{ gap: "var(--space-5)" }} data-testid="pm-costs">
      <PageHead
        crumb={["Project", "Costs"]}
        title="Costs"
        context={<>Recurring and one-time project costs. Monthly total: <span className="sn-num" style={{ color: "var(--text-primary)" }}>{usd2(monthly)}</span></>}
      />

      {canEdit && (
        <Panel title="Add a cost" icon="Plus" data-testid="pm-cost-form">
          <div className="sn-row" style={{ alignItems: "flex-end", gap: "var(--space-3)" }}>
            <TextField label="Item" placeholder="Item" value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} data-testid="pm-cost-item" />
            <TextField label="Category" placeholder="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} style={{ width: 128 }} />
            <TextField label="Amount" type="number" step="0.01" numeric placeholder="Amount" suffix="USD" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} style={{ width: 96 }} data-testid="pm-cost-amount" />
            <Select label="Cadence" options={CADENCE_OPTS} value={form.cadence} onChange={(v) => setForm({ ...form, cadence: v })} width={128} />
            <TextField label="Note" placeholder="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="flex-1" style={{ minWidth: 120 }} />
            <Button variant="primary" icon="Plus" onClick={add} data-testid="pm-cost-add">Add</Button>
          </div>
        </Panel>
      )}

      <div className="sn-grid-12">
        <Panel className="sn-col-8" title="Cost register" icon="DollarSign" flush meta={<span className="sn-sans-num">{rows.length} items</span>}>
          {rows.length === 0 ? (
            <div style={{ padding: "var(--space-4)" }}><EmptyState icon="DollarSign" title="No costs recorded">Add the first cost above.</EmptyState></div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="sn-table">
                <thead><tr><th>Item</th><th>Category</th><th className="r">Amount</th><th>Cadence</th><th>Note</th><th><span className="sn-vh">Actions</span></th></tr></thead>
                <tbody>
                  {rows.map((c) => <CostRow key={c.id} c={c} canEdit={canEdit} refresh={refresh} />)}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
        <Panel className="sn-col-4" title="Monthly by category" icon="Wallet" meta={<span className="sn-num">{usd2(monthly)}</span>}>
          {byCategory.length === 0 ? <span className="sn-small sn-faint">No monthly costs.</span> : (
            <div className="sn-stack" style={{ gap: "var(--space-3)" }}>
              {byCategory.map(([k, v], i) => (
                <div key={k} className="sn-stack" style={{ gap: "var(--space-1)" }}>
                  <div className="sn-row" style={{ justifyContent: "space-between" }}>
                    <span className="sn-small">{k}</span>
                    <span className="sn-num sn-small">{usd2(v)} <span className="sn-faint">· {monthly > 0 ? Math.round((v / monthly) * 100) : 0}%</span></span>
                  </div>
                  <span style={{ height: 4, background: "var(--border-hairline)", borderRadius: 2, position: "relative" }} aria-hidden="true">
                    <i style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${monthly > 0 ? (v / monthly) * 100 : 0}%`, background: RAMP[Math.min(i, RAMP.length - 1)], borderRadius: 2 }} />
                  </span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
